import React from 'react';
import {
  Activity,
  AlertCircle,
  Bell,
  Clock,
  BarChart,
  Shield,
  Server,
  Settings,
  Users,
  Bug,
  Workflow,
  Zap,
  CheckCircle,
  AlertTriangle,
  HelpCircle,
  GitPullRequest
} from 'lucide-react';

const Card = ({ children, className = "" }) => (
  <div className={`bg-white rounded-lg shadow-lg overflow-hidden ${className}`}>
    {children}
  </div>
);

const ApplicationRun = () => {
  const ticketWorkflow = [
    {
      status: "Nouveau",
      icon: Bell,
      description: "Ticket créé dans Mantis",
      actions: [
        "Vérification initiale des informations",
        "Attribution priorité et catégorie",
        "Assignation à l'équipe concernée"
      ],
      sla: "2 heures max"
    },
    {
      status: "En Analyse",
      icon: Bug,
      description: "Investigation technique",
      actions: [
        "Analyse des logs et métriques",
        "Reproduction du problème",
        "Identification de la cause racine"
      ],
      sla: "4 heures max"
    },
    {
      status: "En Traitement",
      icon: Workflow,
      description: "Résolution en cours",
      actions: [
        "Développement de la correction",
        "Tests unitaires et d'intégration",
        "Revue de code"
      ],
      sla: "Selon priorité"
    },
    {
      status: "En Test",
      icon: CheckCircle,
      description: "Validation technique",
      actions: [
        "Déploiement en environnement de test",
        "Tests de non-régression",
        "Validation de la correction"
      ],
      sla: "4 heures max"
    },
    {
      status: "En Production",
      icon: GitPullRequest,
      description: "Déploiement production",
      actions: [
        "Application des correctifs",
        "Vérification post-déploiement",
        "Mise à jour documentation"
      ],
      sla: "2 heures max"
    }
  ];

  const monitoringSetup = [
    {
      aspect: "Infrastructure",
      metrics: [
        "CPU et mémoire",
        "Espace disque",
        "Bande passante réseau",
        "Latence système"
      ],
      alerts: [
        "Seuils CPU/RAM > 80%",
        "Disque > 85%",
        "Latence > 1000ms"
      ]
    },
    {
      aspect: "Application",
      metrics: [
        "Temps de réponse",
        "Taux d'erreur",
        "Sessions actives",
        "Requêtes par seconde"
      ],
      alerts: [
        "Erreurs 5xx > 1%",
        "Temps réponse > 3s",
        "Queue messages > 1000"
      ]
    },
    {
      aspect: "Base de données",
      metrics: [
        "Temps requêtes",
        "Connexions actives",
        "Taille des tables",
        "Locks et deadlocks"
      ],
      alerts: [
        "Requêtes lentes > 5s",
        "Connexions > 80%",
        "Locks > 1 minute"
      ]
    }
  ];

  const supportLevels = [
    {
      level: "N1",
      team: "Support Client",
      responsibilities: [
        "Réception et qualification des tickets",
        "Résolution incidents simples",
        "Escalade vers N2 si nécessaire",
        "Suivi des tickets clients"
      ],
      sla: "Réponse initiale < 30 min"
    },
    {
      level: "N2",
      team: "Support Technique",
      responsibilities: [
        "Analyse technique approfondie",
        "Résolution incidents complexes",
        "Maintenance préventive",
        "Support N1 et clients"
      ],
      sla: "Prise en charge < 2h"
    },
    {
      level: "N3",
      team: "Équipe Développement",
      responsibilities: [
        "Résolution bugs applicatifs",
        "Optimisation performance",
        "Evolution fonctionnelle",
        "Support N2"
      ],
      sla: "Selon criticité"
    }
  ];

  const maintenanceProcedures = [
    {
      type: "Préventive",
      frequency: "Mensuelle",
      tasks: [
        "Analyse des métriques système",
        "Nettoyage des logs",
        "Optimisation base de données",
        "Mise à jour des patchs"
      ]
    },
    {
      type: "Corrective",
      frequency: "Sur incident",
      tasks: [
        "Diagnostic rapide",
        "Application correctifs",
        "Tests de validation",
        "Documentation intervention"
      ]
    },
    {
      type: "Évolutive",
      frequency: "Trimestrielle",
      tasks: [
        "Déploiement nouvelles versions",
        "Migration données",
        "Mise à jour documentation",
        "Formation utilisateurs"
      ]
    }
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      <Card>
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 p-6">
          <h2 className="text-2xl font-bold text-center text-white">
            RUN et Support Applications
          </h2>
          <p className="text-center text-white mt-2 opacity-90">
            Gestion avec Mantis Cloud
          </p>
        </div>

        <div className="p-6 space-y-8">
          {/* Workflow des Tickets */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Workflow className="mr-2" />
              Workflow des Tickets
            </h3>
            <div className="space-y-4">
              {ticketWorkflow.map((stage, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-start">
                    <div className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white mr-4">
                      <stage.icon className="w-6 h-6" />
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center justify-between">
                        <h4 className="font-medium text-lg">{stage.status}</h4>
                        <span className="text-sm text-gray-600">SLA: {stage.sla}</span>
                      </div>
                      <p className="text-sm text-gray-600 mb-3">{stage.description}</p>
                      <div className="space-y-2">
                        {stage.actions.map((action, idx) => (
                          <div key={idx} className="flex items-center text-sm text-gray-600">
                            <CheckCircle className="w-4 h-4 mr-2 text-green-500" />
                            {action}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Monitoring */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Activity className="mr-2" />
              Configuration Monitoring
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {monitoringSetup.map((item, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-600 mb-3">{item.aspect}</h4>
                  <div className="space-y-4">
                    <div>
                      <h5 className="text-sm font-medium mb-2">Métriques</h5>
                      <ul className="space-y-2">
                        {item.metrics.map((metric, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <BarChart className="w-4 h-4 mr-2 text-blue-500" />
                            {metric}
                          </li>
                        ))}
                      </ul>
                    </div>
                    <div>
                      <h5 className="text-sm font-medium mb-2">Alertes</h5>
                      <ul className="space-y-2">
                        {item.alerts.map((alert, idx) => (
                          <li key={idx} className="flex items-center text-sm text-gray-600">
                            <AlertCircle className="w-4 h-4 mr-2 text-orange-500" />
                            {alert}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Niveaux de Support */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Users className="mr-2" />
              Organisation Support
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {supportLevels.map((level, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="font-medium text-blue-600">{level.level}</h4>
                    <span className="text-sm text-gray-600">{level.team}</span>
                  </div>
                  <div className="space-y-3">
                    <ul className="space-y-2">
                      {level.responsibilities.map((resp, idx) => (
                        <li key={idx} className="flex items-start text-sm text-gray-600">
                          <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500" />
                          {resp}
                        </li>
                      ))}
                    </ul>
                    <div className="text-sm text-gray-600">
                      <span className="font-medium">SLA:</span> {level.sla}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Procédures de Maintenance */}
          <div>
            <h3 className="text-xl font-semibold mb-4 flex items-center">
              <Settings className="mr-2" />
              Procédures de Maintenance
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {maintenanceProcedures.map((proc, index) => (
                <div key={index} className="bg-gray-50 p-4 rounded-lg border border-blue-200">
                  <h4 className="font-medium text-blue-600 mb-2">{proc.type}</h4>
                  <p className="text-sm text-gray-600 mb-3">Fréquence: {proc.frequency}</p>
                  <ul className="space-y-2">
                    {proc.tasks.map((task, idx) => (
                      <li key={idx} className="flex items-start text-sm text-gray-600">
                        <CheckCircle className="w-4 h-4 mr-2 mt-0.5 text-green-500" />
                        {task}
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          </div>

          {/* Points d'Attention */}
          <div className="bg-yellow-50 p-4 rounded-lg border border-yellow-200">
            <div className="flex items-start">
              <AlertTriangle className="w-5 h-5 text-yellow-500 mr-3 flex-shrink-0 mt-0.5" />
              <div>
                <h4 className="font-semibold text-yellow-700 mb-2">Points d'Attention</h4>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-yellow-800">
                  <div className="space-y-2">
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Toujours respecter les SLAs définis
                    </p>
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Maintenir la documentation à jour
                    </p>
                  </div>
                  <div className="space-y-2">
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Escalader rapidement si nécessaire
                    </p>
                    <p className="flex items-center">
                      <span className="text-yellow-500 mr-2">•</span>
                      Backup avant toute intervention
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default ApplicationRun;